import React from 'react'
import { useTranslation } from 'react-i18next'
import { H3, Button, Separator, Paragraph, InfoSheetPanel, Row, Overline, usePageWidth } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import {
  getNestedElementById,
  getNestedFloatingElementsByType,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useFamilyReviewStateContext } from '../../providers/FamilyReviewContext'
import EventReviewCard from '../eventReviewCard/EventReviewCard'
import { showAllNamesAndEventsHighlights, showEssentialInfoHighlights } from '../../utils/highlightUtils'

export default function ReviewPersonEventsPanel({
  currentTask,
  addNewEventOverlay,
  addFloatingEventOverlay,
  editEventOverlay,
  editPersonOverlay,
  reviewPersonEventsOverlay,
}) {
  const h = useSlsHighlightsContext()
  const [t] = useTranslation()
  const atPageWidth = usePageWidth()
  const isSmallScreen = atPageWidth({ default: true, md: false })
  const zionSLSState = useSlsStateContext()
  const state = useFamilyReviewStateContext()

  const saveButtonClicked = () => {
    const allPersons = currentTask.subElements.filter((e) => e.elementType === 'PERSON')
    const personIds = allPersons.flatMap((person) => person?.id)
    showAllNamesAndEventsHighlights({
      h,
      currentTask,
      highlights: [...h.getHighlights().mainEditHighlights, ...h.getHighlights().splitScreenEditHighlights],
      personIds,
      isSmallScreen,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
    })
    reviewPersonEventsOverlay.close()
  }

  const handleNewEvent = () => {
    const imageHasFloatingEvents = getNestedFloatingElementsByType(zionSLSState.images[0], 'EVENT').length !== 0
    if (imageHasFloatingEvents) addFloatingEventOverlay.open()
    else addNewEventOverlay.open()
    h.clearDisplay({ all: true })
  }

  const handleClickingAddEvent = () => {
    reviewPersonEventsOverlay.close()
    handleNewEvent()
  }
  const personEvents = currentTask?.subElements
    ?.filter((se) => se?.elementType === 'PERSON')
    .filter((person) => person?.id === state.selectedPersonId)
    .flatMap((person) => person?.subElements?.filter((el) => el.elementType === 'EVENT'))
    .map((event) => ({ eventId: event?.id, hasBeenReviewed: true }))

  const resetHighlights = () => {
    showEssentialInfoHighlights({
      h,
      mainPartitionKey: currentTask.partitionKey,
      splitScreenPartitionKey: state.splitScreenPartitionKey,
      personIds: [state.selectedPersonId],
      isSmallScreen,
    })
  }

  const handleCloseReviewPersonEvents = () => {
    reviewPersonEventsOverlay.close()
    resetHighlights()
  }

  const cancelButtonButtonClicked = () => {
    handleCloseReviewPersonEvents()
    editPersonOverlay.open()
  }

  const userClosedPanel = () => {
    handleCloseReviewPersonEvents()
  }

  return (
    <InfoSheetPanel
      {...{ ...reviewPersonEventsOverlay, close: userClosedPanel }}
      title={t('family-review.step5.heading', 'Final Review')}
      headingColor="purple"
      footer={
        <Row alignX="end">
          <Button
            onClick={cancelButtonButtonClicked}
            linkName="Review Person Events Cancel"
            data-testid="review-person-events-cancel-button"
          >
            {t('edit-index.cancel.button', 'Cancel')}
          </Button>
          <Button
            emphasis="high"
            onClick={saveButtonClicked}
            linkName="Review Person Events Save"
            data-testid="review-person-events-save-button"
          >
            {t('edit-index.save.button', 'Save')}
          </Button>
        </Row>
      }
    >
      {reviewPersonEventsOverlay.isOpen && (
        <>
          <H3 size="H5">
            <Overline>{t('family-review.stepXof4', 'Step {step} of 4', { step: '4' })}</Overline>
            {t('family-review.editEvents.heading', 'Edit Events')}
          </H3>
          <Paragraph size="sm">
            {t(
              'family-review.reviewPersonEventsPanel.content',
              'Review the information below. Select the pencil icon to edit the information.'
            )}
          </Paragraph>
          <Separator size="sm" />
          {personEvents.map((cardState, index, array) => {
            const event = getNestedElementById(currentTask?.subElements, cardState.eventId)
            return (
              <div key={`${cardState.eventId}-card`}>
                {event && (
                  <>
                    <EventReviewCard
                      event={event}
                      currentTask={currentTask}
                      editEventOverlay={editEventOverlay}
                      reviewPersonEventsOverlay={reviewPersonEventsOverlay}
                    />
                    {index < array.length - 1 && <Separator size="sm" />}
                  </>
                )}
              </div>
            )
          })}
          <Separator size="sm" />
          <Button
            Icon={ContentAdd}
            onClick={handleClickingAddEvent}
            data-testid="add-event"
            linkName="Review Person Events Add Event"
          >
            {t('edit-index.add-event.button', 'Add Event')}
          </Button>
        </>
      )}
    </InfoSheetPanel>
  )
}
